import { useAuthentication } from '@/hooks/useAuthentication';
import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from '@/components/ui/use-toast';
import { importStaff } from '@/services/global/admin.service';
import { useManageStaffStore } from '../store/useManageStaffStore';

export type RowData = { surname: string; forename: string; email: string };

type UseImportStaffParams = {
  refetch?: () => Promise<void> | void;
  setOpen: React.Dispatch<boolean>;
  rowsPerPage?: number;
};

export const useImportStaff = ({ refetch, setOpen, rowsPerPage = 10 }: UseImportStaffParams) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const { schoolContext } = useAuthentication();
  const [data, setData] = useState<RowData[]>([
    {
      surname: 'John',
      forename: 'Doe',
      email: 'john.doe@email.com',
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const dialogData = useManageStaffStore((state) => state?.page?.staffImportFromSpreadsheetDialog);
  const [spreadsheetUploaded, setSpreadsheetUploaded] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const file = event.target.files[0];

    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target) return;
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });

      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      const jsonData = XLSX.utils.sheet_to_json<{
        surname: string;
        Surname: string;
        forename: string;
        Forename: string;
        email: string;
        Email: string;
      }>(worksheet);

      const filteredData = jsonData.map((row) => ({
        surname: row.surname || row.Surname,
        forename: row.forename || row.Forename,
        email: row.email || row.Email,
      }));

      if (filteredData.some((row) => !row.email)) {
        toast({
          description: 'Some rows are missing email address',
          variant: 'destructive',
        });
        if (fileInput.current) {
          fileInput.current.value = '';
        }
        return;
      }

      setData(filteredData);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
      setSpreadsheetUploaded(true);
    };
    reader.readAsBinaryString(file);
  };

  const onImportStaff = async (users?: RowData[]) => {
    setLoadingImport(true);
    const { data, error } = await importStaff(schoolContext?.documentId || '', users);

    if (error) {
      toast({
        title: dialogData?.toastStaffImportedTitle || '',
        description: error.message,
        variant: 'destructive',
      });
      setLoadingImport(false);
      return;
    }

    toast({
      title: dialogData?.toastStaffImportedTitle || '',
      description: `${data.size} ${dialogData?.toastStaffImportedDescription || ''}`,
    });
    if (refetch) {
      await refetch();
    }
    setLoadingImport(false);
  };

  const handleImportStaff = async () => {
    setIsLoading(true);
    await onImportStaff(data);
    setOpen(false);
    setIsLoading(false);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const totalPages = Math.ceil(data.length / rowsPerPage);
  const paginatedData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  return {
    fileInput,
    totalPages,
    paginatedData,
    handleFileUpload,
    handleImportStaff,
    handlePageChange,
    spreadsheetUploaded,
    loadingImport,
    isLoading,
    currentPage,
  };
};
